import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useConfirmDialog } from '../components/ConfirmDialog';
import { connectSocket } from '../utils/socket';
import api, { chatApi } from '../utils/api';
import './Group.css';

interface GroupDetails {
  id: number;
  title: string;
  updated_at: string;
  is_active: boolean;
  group_created_at: string;
}

const GroupPage: React.FC = () => {
  const navigate = useNavigate();
  const { storyId } = useParams<{ storyId: string }>();
  const { user } = useAuth();
  const { confirm, confirmDialog } = useConfirmDialog();
  const [group, setGroup] = useState<GroupDetails | null>(null);
  const [isOwner, setIsOwner] = useState(false);
  const [hasScreenplayAccess, setHasScreenplayAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [chatUnread, setChatUnread] = useState(0);
  const [deletingGroup, setDeletingGroup] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState('');

  useEffect(() => {
    loadGroup();
    checkOwnership();
    checkScreenplayAccess();
    loadChatUnread();
  }, [storyId]);

  // ── Real-time chat unread badge ───────────────────────────
  useEffect(() => {
    if (!user?.id) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    const socket = connectSocket(token);

    const handleNewGroupMessage = (message: any) => {
      if (message.user_id === user.id) return;
      if (message.story_id !== Number(storyId)) return;
      setChatUnread(prev => prev + 1);
    };

    socket.on('new-message', handleNewGroupMessage);

    return () => {
      socket.off('new-message', handleNewGroupMessage);
    };
  }, [user?.id, storyId]);

  const loadGroup = async () => {
    try {
      const response = await api.get(`/groups/${storyId}`);
      setGroup(response.data.group);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load group');
    } finally {
      setLoading(false);
    }
  };

  const handleTitleEdit = () => {
    if (!group) return;
    setTempTitle(group.title);
    setIsEditingTitle(true);
    setError('');
  };

  const handleTitleCancel = () => {
    setTempTitle(group?.title || '');
    setIsEditingTitle(false);
  };

  const handleTitleSave = async () => {
    if (!group) return;
    if (!tempTitle.trim()) {
      setError('Title cannot be empty');
      return;
    }

    try {
      await api.put(`/stories/${group.id}/title`, { title: tempTitle });
      setGroup({ ...group, title: tempTitle });
      setMessage('Title updated');
      setError('');
      setIsEditingTitle(false);
      setTimeout(() => setMessage(''), 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update title');
    }
  };

  const checkOwnership = async () => {
    try {
      const response = await api.get(`/groups/${storyId}/ownership`);
      setIsOwner(response.data.isOwner);
    } catch (err) {
      console.error('Failed to check ownership:', err);
    }
  };

  const checkScreenplayAccess = async () => {
    try {
      const response = await api.get(`/stories/${storyId}/screenplay-access/me`);
      setHasScreenplayAccess(Boolean(response.data.hasAccess));
    } catch (err) {
      console.error('Failed to check screenplay access:', err);
      setHasScreenplayAccess(false);
    }
  };

  const loadChatUnread = async () => {
    try {
      const response = await chatApi.get('/chat/unread-counts');
      const counts: Record<number, number> = response.data.counts || {};
      setChatUnread(counts[Number(storyId)] ?? 0);
    } catch (err) {
      console.error('Failed to load chat unread count:', err);
    }
  };

  const handleDeleteGroup = async () => {
    if (!isOwner || !storyId || deletingGroup) return;

    const approved = await confirm({
      title: 'Delete Group',
      message: 'Delete this active project group and move the story back to drafts?',
      confirmLabel: 'Delete Group',
      cancelLabel: 'Keep Group',
      danger: true,
    });

    if (!approved) return;

    setDeletingGroup(true);
    setError('');
    try {
      await api.delete(`/groups/${storyId}`);
      setMessage('Group deleted. Story moved back to drafts.');
      setTimeout(() => navigate('/home'), 700);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete group');
    } finally {
      setDeletingGroup(false);
    }
  };

  if (loading) {
    return (
      <div className="group-container">
        <div className="loading">Loading group…</div>
      </div>
    );
  }

  if (error || !group) {
    return (
      <div className="group-container">
        <div className="error-message">{error || 'Group not found'}</div>
        <button onClick={() => navigate('/content')} className="back-btn">
          ← Back to Home
        </button>
      </div>
    );
  }

  return (
    <div className="group-container">
      {confirmDialog}
      <div className="group-header">
        <button onClick={() => navigate('/home')} className="back-btn">
          ← Back
        </button>
        <div className="group-title-wrapper">
          {isEditingTitle ? (
            <div className="group-title-edit">
              <input
                type="text"
                value={tempTitle}
                onChange={(e) => setTempTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleTitleSave();
                  if (e.key === 'Escape') handleTitleCancel();
                }}
                className="group-title-input"
                autoFocus
              />
              <button onClick={handleTitleSave} className="group-title-save-btn">✓</button>
              <button onClick={handleTitleCancel} className="group-title-cancel-btn">✗</button>
            </div>
          ) : (
            <div className="group-title-display">
              <h1>{group.title}</h1>
              {isOwner && (
                <button onClick={handleTitleEdit} className="group-title-edit-btn" aria-label="Edit group title">✏</button>
              )}
            </div>
          )}
        </div>
        {isOwner && <span className="owner-badge">Owner</span>}
      </div>

      <div className="group-info">
  <p>
    Group started{" "}
    {new Date(group.group_created_at).toLocaleDateString("en-GB")}
  </p>
</div>
      {error && <div className="error-message">{error}</div>}
      {message && <div className="success-message">{message}</div>}

      <div className="group-options">

        {/* Story / Screenplay */}
        <div className="option-card" onClick={() => navigate(`/story/${storyId}`)}>
          <div className="option-icon">Story</div>
          <h3>{isOwner ? 'Edit Story' : 'View Story'}</h3>
          <p>
            {isOwner
              ? 'Edit screenplay and story content'
              : hasScreenplayAccess
                ? 'Read story and edit screenplay'
                : 'Read story and screenplay'}
          </p>
          {!isOwner && <span className="view-only-badge">{hasScreenplayAccess ? 'Editor' : 'View Only'}</span>}
        </div>

        {/* Chat — with unread badge */}
        <div className="option-card" style={{ position: 'relative' }} onClick={() => navigate(`/chat/${storyId}`)}>
          {chatUnread > 0 && (
            <span className="group-card-unread-badge">{chatUnread}</span>
          )}
          <div className="option-icon">Chat</div>
          <h3>Chat</h3>
          <p>Group discussions and updates</p>
        </div>

        {/* Cast & Crew */}
        <div className="option-card" onClick={() => navigate(`/cast-crew/${storyId}`)}>
          <div className="option-icon">Cast</div>
          <h3>Cast &amp; Crew</h3>
          <p>View all group members</p>
        </div>

        {/* Clips */}
        <div className="option-card" onClick={() => navigate(`/clips/${storyId}`)}>
          <div className="option-icon">Clips</div>
          <h3>Clips</h3>
          <p>Upload and manage scene videos</p>
        </div>

        {/* Auditions — owner only */}
        {isOwner ? (
          <div className="option-card" onClick={() => navigate(`/auditions/${storyId}`)}>
            <div className="option-icon">Auditions</div>
            <h3>Auditions</h3>
            <p>Manage casting and auditions</p>
            <span className="owner-only-badge">Owner Only</span>
          </div>
        ) : (
          <div className="option-card disabled">
            <div className="option-icon">Auditions</div>
            <h3>Auditions</h3>
            <p>Manage casting and auditions</p>
            <span className="owner-only-badge">Owner Only</span>
          </div>
        )}

        {/* Schedule */}
        <div className="option-card" onClick={() => navigate(`/schedule/${storyId}`)}>
          <div className="option-icon">Schedule</div>
          <h3>Schedule</h3>
          <p>Production calendar and planning</p>
          <span className="owner-only-badge">{isOwner ? 'Owner Controls' : 'View Only'}</span>
        </div>

      </div>

      {isOwner && (
        <section className="group-danger-zone">
          <div>
            <h2>Delete Group</h2>
            <p>Remove the active collaboration group and return this project to Story Drafts.</p>
          </div>
          <button
            className="group-delete-btn"
            type="button"
            onClick={handleDeleteGroup}
            disabled={deletingGroup}
          >
            {deletingGroup ? 'Deleting...' : 'Delete Group'}
          </button>
        </section>
      )}
    </div>
  );
};

export default GroupPage;
